import { useRef, useState, useCallback, useEffect } from 'react';
import {
  collectActiveFormatsFromAncestors,
  isFormatActiveAtRange,
  normalizeLinkHref,
  WYSIWYG_COMMAND_TO_TAG,
} from '../utils/wysiwygEditorUtils';

/**
 * Hook personalizado para editor WYSIWYG
 *
 * Implementación usando Selection API y Range API nativas del navegador.
 *
 * **Ventajas de usar Selection API vs document.execCommand (deprecado):**
 * - ✅ Control total sobre el HTML generado (consistente entre navegadores)
 * - ✅ APIs modernas y mantenidas
 * - ✅ Mayor flexibilidad para personalizar comportamientos
 * - ✅ Mejor manejo de estados de formato
 * - ✅ No dependiente de APIs deprecadas
 *
 * **Cómo funciona:**
 * - `window.getSelection()`: Obtiene la selección actual del usuario
 * - `Range`: Define el rango de texto seleccionado
 * - `surroundContents()`: Envuelve la selección en un elemento HTML
 * - `extractContents()`: Extrae el contenido de la selección
 * - `insertNode()`: Inserta un nodo en la posición del cursor
 *
 * @returns Objeto con refs, estados y funciones del editor
 */
export const useWysiwyg = () => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());

  const updateActiveFormats = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const formats = collectActiveFormatsFromAncestors(range, editorRef.current);
    if (formats === null) return;

    setActiveFormats(formats);
  }, []);

  // Aplicar formato usando Selection API
  const applyFormat = useCallback(
    (tag: string, style?: Partial<CSSStyleDeclaration>) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      if (range.collapsed) {
        // Si no hay selección, no hacer nada
        return;
      }

      const element = document.createElement(tag);
      if (style) {
        Object.assign(element.style, style);
      }

      try {
        range.surroundContents(element);
      } catch (e) {
        // Si surroundContents falla (rango complejo), usar extractContents
        const fragment = range.extractContents();
        element.appendChild(fragment);
        range.insertNode(element);
      }

      // Limpiar selección y actualizar
      selection.removeAllRanges();
      updateActiveFormats();
      editorRef.current?.focus();
    },
    [updateActiveFormats],
  );

  // Remover formato usando Selection API
  const removeFormat = useCallback(
    (tag: string) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      let node: Node | null = range.commonAncestorContainer;

      // Buscar el nodo del formato a remover
      while (node && node !== editorRef.current) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          if (element.tagName.toLowerCase() === tag.toLowerCase()) {
            // Reemplazar el elemento con su contenido
            const parent = element.parentNode;
            while (element.firstChild) {
              parent?.insertBefore(element.firstChild, element);
            }
            parent?.removeChild(element);
            break;
          }
        }
        node = node.parentNode;
      }

      updateActiveFormats();
      editorRef.current?.focus();
    },
    [updateActiveFormats],
  );

  const isFormatActive = useCallback((tag: string): boolean => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;

    const range = selection.getRangeAt(0);
    return isFormatActiveAtRange(tag, range, editorRef.current);
  }, []);

  // Ejecutar comando de formato
  const execCommand = useCallback(
    (command: string, value?: string) => {
      const tag = WYSIWYG_COMMAND_TO_TAG[command];

      if (tag) {
        // Verificar si el formato ya está activo
        if (isFormatActive(command === 'strikeThrough' ? 'strikethrough' : command)) {
          removeFormat(tag);
        } else {
          applyFormat(tag);
        }
      } else if (command === 'insertUnorderedList') {
        toggleList('ul');
      } else if (command === 'insertOrderedList') {
        toggleList('ol');
      } else if (command === 'createLink' && value) {
        createLink(value);
      } else {
        // Fallback para comandos no implementados
        document.execCommand(command, false, value);
      }

      updateActiveFormats();
      editorRef.current?.focus();
    },
    // createLink y toggleList se definen más abajo; incluirlos crearía dependencia circular
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applyFormat, removeFormat, isFormatActive, updateActiveFormats],
  );

  // Crear enlace
  const createLink = useCallback(
    (url: string) => {
      if (!editorRef.current) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      if (range.collapsed) {
        alert('Por favor selecciona el texto que quieres convertir en enlace');
        return;
      }

      const validUrl = normalizeLinkHref(url);
      if (!validUrl) return;

      const link = document.createElement('a');
      link.href = validUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.color = '#3658c1';
      link.style.textDecoration = 'underline';

      try {
        range.surroundContents(link);
      } catch (e) {
        const fragment = range.extractContents();
        link.appendChild(fragment);
        range.insertNode(link);
      }

      // Mover el cursor después del enlace
      const newRange = document.createRange();
      newRange.setStartAfter(link);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);

      editorRef.current?.focus();
      updateActiveFormats();
    },
    [updateActiveFormats],
  );

  // Toggle lista (ul/ol) - Implementación manual mejorada
  const toggleList = useCallback(
    (listType: 'ul' | 'ol') => {
      if (!editorRef.current) return;

      const selection = window.getSelection();
      if (!selection) return;

      // Buscar si ya estamos en una lista
      const currentNode: Node | null =
        selection.rangeCount > 0
          ? selection.getRangeAt(0).commonAncestorContainer
          : editorRef.current;

      let existingList: HTMLElement | null = null;
      let tempNode: Node | null = currentNode;

      while (tempNode && tempNode !== editorRef.current) {
        if (tempNode.nodeType === Node.ELEMENT_NODE) {
          const element = tempNode as HTMLElement;
          const tagName = element.tagName.toLowerCase();
          if (tagName === 'ul' || tagName === 'ol') {
            existingList = element;
            break;
          }
        }
        tempNode = (tempNode.parentNode as Node) || null;
      }

      if (existingList) {
        // Ya hay una lista, removerla o convertirla
        const existingType = existingList.tagName.toLowerCase();

        if (existingType === listType) {
          // Misma lista, removerla
          const parent = existingList.parentNode;
          if (parent) {
            Array.from(existingList.children).forEach((li) => {
              const div = document.createElement('div');
              div.innerHTML = li.innerHTML || '<br>';
              parent.insertBefore(div, existingList);
            });
            parent.removeChild(existingList);
          }
        } else {
          // Diferente tipo, convertir
          const newList = document.createElement(listType);
          newList.innerHTML = existingList.innerHTML;
          existingList.parentNode?.replaceChild(newList, existingList);
        }
      } else {
        // No hay lista, crear una nueva
        const list = document.createElement(listType);
        const li = document.createElement('li');

        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);

          if (range.collapsed) {
            // Sin selección, crear lista vacía
            li.innerHTML = '<br>';
            list.appendChild(li);

            if (
              editorRef.current.childNodes.length === 0 ||
              editorRef.current.textContent?.trim() === ''
            ) {
              editorRef.current.innerHTML = '';
              editorRef.current.appendChild(list);
            } else {
              range.insertNode(list);
            }

            const newRange = document.createRange();
            newRange.setStart(li, 0);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
          } else {
            // Hay selección, extraer contenido
            const fragment = range.extractContents();

            if (fragment.textContent?.trim() === '') {
              li.innerHTML = '<br>';
            } else {
              li.appendChild(fragment);
            }

            list.appendChild(li);
            range.insertNode(list);

            const newRange = document.createRange();
            newRange.selectNodeContents(li);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        } else {
          // Sin range, agregar al final
          li.innerHTML = '<br>';
          list.appendChild(li);
          editorRef.current.appendChild(list);

          const newRange = document.createRange();
          newRange.setStart(li, 0);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }

      setTimeout(() => {
        updateActiveFormats();
      }, 10);

      editorRef.current?.focus();
    },
    [updateActiveFormats],
  );

  // Manejar click en el editor
  const handleEditorClick = useCallback(() => {
    updateActiveFormats();
  }, [updateActiveFormats]);

  // Manejar selección
  const handleSelectionChange = useCallback(() => {
    if (isFocused) {
      updateActiveFormats();
    }
  }, [isFocused, updateActiveFormats]);

  // Escuchar cambios en la selección
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleSelectionChange]);

  // Insertar texto usando Selection API
  const insertText = useCallback((text: string) => {
    if (!editorRef.current) return;

    editorRef.current.focus();
    const selection = window.getSelection();

    if (!selection) return;

    let range: Range;

    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
      range.deleteContents();
    } else {
      // Si no hay selección, crear una al final del editor
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    // Mover el cursor después del texto insertado
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  // Insertar HTML usando Selection API
  const insertHTML = useCallback((html: string) => {
    if (!editorRef.current) return;

    editorRef.current.focus();
    const selection = window.getSelection();

    if (!selection) return;

    let range: Range;

    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
      range.deleteContents();
    } else {
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    const fragment = range.createContextualFragment(html);
    range.insertNode(fragment);

    // Mover el cursor después del contenido insertado
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  // Insertar emoji
  const insertEmoji = useCallback(
    (emoji: string) => {
      insertText(emoji);
    },
    [insertText],
  );

  // Insertar mención
  const insertMention = useCallback(
    (mention: string) => {
      insertHTML(`<span contenteditable="false" class="mention">@${mention}</span>&nbsp;`);
    },
    [insertHTML],
  );

  // Obtener contenido HTML
  const getHTML = useCallback(() => {
    return editorRef.current?.innerHTML || '';
  }, []);

  // Establecer contenido HTML
  const setHTML = useCallback((html: string) => {
    if (editorRef.current) {
      editorRef.current.innerHTML = html;
    }
  }, []);

  // Limpiar contenido
  const clear = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  }, []);

  // Verificar si está vacío
  const isEmpty = useCallback(() => {
    const text = editorRef.current?.textContent || '';
    return text.trim().length === 0;
  }, []);

  return {
    editorRef,
    isFocused,
    setIsFocused,
    activeFormats,
    execCommand,
    updateActiveFormats,
    handleEditorClick,
    insertEmoji,
    insertMention,
    getHTML,
    setHTML,
    clear,
    isEmpty,
  };
};
